/**
 * The usage log in React: the router listener and two small hooks.
 *
 * `UsageTracker` is mounted once, in `ProtectedRoute`, so it runs on every signed-in
 * page and never on the sign-in page. It reports which page a person opens and leaves
 * (with the active time on it), how long a page takes to be ready, and the errors a
 * person sees. `useDialogUsage` reports a dialog opened, saved, or closed without a
 * save. `useTrackOnce` reports one event one time for a key (a case id).
 *
 * The names are in `lib/usage-events.ts`. The queue, the transport and the rule "fail
 * silent" are in `lib/usage.ts`. Nothing here renders, and nothing here can break a page.
 */

import { isCancelledError } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router";

import { type LiveUserProfile, queryClient } from "@/lib/api";
import { logger } from "@/lib/logger";
import {
  type ActiveClock,
  createActiveClock,
  flushUsage,
  setUsagePage,
  startUsage,
  stopUsage,
  track,
  type TrackOptions,
} from "@/lib/usage";
import {
  caseIdOf,
  routeTemplate,
  searchShape,
  slugOf,
  USAGE_DIALOG_EVENTS,
  USAGE_REQUEST_KEYS,
  type UsageDetail,
  type UsageDialog,
  type UsageEventName,
  type UsageRoute,
  usageWord,
} from "@/lib/usage-events";

/** The first look at "is the page ready" after a page view. */
const READY_FIRST_CHECK_MS = 50;
/** A page that is not ready after this time is not reported. */
const READY_MAX_MS = 30_000;
/** What counts as "the person did something" for the active time. */
const INPUT_EVENTS = ["pointerdown", "keydown", "wheel", "scroll", "focus"] as const;

type Nav = "load" | "route";

interface ReadyWatch {
  startedAt: number;
  nav: Nav;
  /** The first check ran: cache events count from here. */
  armed: boolean;
  /** When no query was fetching for the first time. */
  readyAt: number | null;
  done: boolean;
}

interface TrackerState {
  pathname: string | null;
  route: UsageRoute | null;
  /** The route of the last page view, for `from`. */
  lastRoute: UsageRoute | null;
  /** A visible stretch of the page is open (a `page.leave` is due). */
  open: boolean;
  clock: ActiveClock;
  ready: ReadyWatch | null;
  stopTimer: number | null;
}

/** One `page.view` with `nav: "load"` per document. */
let documentViewed = false;
/** One `perf.page_load` per document. */
let pageLoadReported = false;

function isHidden(): boolean {
  return document.visibilityState === "hidden";
}

/** End the visible stretch of the page: one `page.leave` with its active time. */
function closeStretch(state: TrackerState, reason: "route" | "hidden" | "close"): void {
  if (!state.open || state.route === null) {
    return;
  }
  state.open = false;
  const { activeMs, wallMs } = state.clock.read();
  track("page.leave", { reason, wall_ms: wallMs }, { durationMs: activeMs });
}

/** Start a visible stretch (a new page, or the same page seen again). */
function openStretch(state: TrackerState): void {
  if (state.open || state.route === null || isHidden()) {
    return;
  }
  state.clock.start();
  state.open = true;
}

/** `perf.page_ready`: the time from the page view until no query is fetching. */
function checkReady(state: TrackerState, watch: ReadyWatch): void {
  if (watch.done || state.ready !== watch) {
    return;
  }
  const now = performance.now();
  if (isHidden() || now - watch.startedAt > READY_MAX_MS) {
    watch.done = true;
    return;
  }
  if (queryClient.isFetching() > 0) {
    watch.readyAt = null;
    return;
  }
  if (watch.readyAt === null) {
    // Look once more a moment later: a second read often starts when the first ends.
    watch.readyAt = now;
    window.setTimeout(() => checkReady(state, watch), READY_FIRST_CHECK_MS);
    return;
  }
  watch.done = true;
  track("perf.page_ready", { nav: watch.nav }, { durationMs: watch.readyAt - watch.startedAt });
}

function watchReady(state: TrackerState, nav: Nav): void {
  const watch: ReadyWatch = {
    startedAt: performance.now(),
    nav,
    armed: false,
    readyAt: null,
    done: false,
  };
  state.ready = watch;
  window.setTimeout(() => {
    watch.armed = true;
    checkReady(state, watch);
  }, READY_FIRST_CHECK_MS);
}

/** `perf.page_load`: once per document, from the Navigation Timing entry. */
function reportPageLoad(): void {
  if (pageLoadReported) {
    return;
  }
  pageLoadReported = true;
  const report = () => {
    try {
      const entry = performance.getEntriesByType("navigation")[0] as
        | PerformanceNavigationTiming
        | undefined;
      if (!entry || !(entry.domContentLoadedEventEnd > 0)) {
        return;
      }
      track(
        "perf.page_load",
        {
          ttfb_ms: entry.responseStart,
          load_ms: entry.loadEventEnd > 0 ? entry.loadEventEnd : undefined,
        },
        { durationMs: entry.domContentLoadedEventEnd, caseId: null },
      );
    } catch (error) {
      logger.debug("[usage]", "no navigation timing", error);
    }
  };
  if (document.readyState === "complete") {
    window.setTimeout(report, 0);
  } else {
    // The load event ends after its listeners: read the entry one task later.
    window.addEventListener("load", () => window.setTimeout(report, 0), { once: true });
  }
}

/** The HTTP status of a failed request; `undefined` when there is none. */
function statusOf(error: unknown): number | undefined {
  const status = (error as { status?: unknown } | null | undefined)?.status;
  return typeof status === "number" && Number.isInteger(status) && status >= 0 ? status : undefined;
}

/**
 * The first part of a query or mutation key, when it is on the list of request keys.
 * A key outside the list gives no `what`: the failure is still recorded.
 */
function whatOf(key: readonly unknown[] | undefined): string | undefined {
  const first = key?.[0];
  return usageWord(USAGE_REQUEST_KEYS, typeof first === "string" ? first : undefined);
}

/**
 * What the log keeps of a failed request: the status and the first part of the key.
 * `null` for a sign-out (401) and for a cancelled request: neither is an error.
 */
function failureDetail(error: unknown, key: readonly unknown[] | undefined): UsageDetail | null {
  const status = statusOf(error);
  if (status === 401 || isCancelledError(error)) {
    return null;
  }
  return { status, what: whatOf(key) };
}

/**
 * The router listener of the usage log. It renders nothing. With a signed-in person it
 * starts the log; on each new path it reports the page left and the page opened.
 */
export function UsageTracker({ me }: { me: LiveUserProfile | undefined }): null {
  const { pathname } = useLocation();
  const signedIn = me !== undefined;
  const stateRef = useRef<TrackerState | null>(null);

  useEffect(() => {
    if (!signedIn) {
      return undefined;
    }
    stateRef.current ??= {
      pathname: null,
      route: null,
      lastRoute: null,
      open: false,
      clock: createActiveClock(() => Date.now()),
      ready: null,
      stopTimer: null,
    };
    const state = stateRef.current;
    // StrictMode runs an effect twice: the stop of the first run is called off here.
    if (state.stopTimer !== null) {
      window.clearTimeout(state.stopTimer);
      state.stopTimer = null;
    }
    startUsage();
    reportPageLoad();

    const onInput = () => state.clock.input();
    const onVisibility = () => {
      if (isHidden()) {
        if (state.ready) {
          state.ready.done = true;
        }
        closeStretch(state, "hidden");
        flushUsage("hidden");
      } else {
        openStretch(state);
      }
    };
    const onPageHide = () => {
      closeStretch(state, "close");
      flushUsage("pagehide");
    };
    const onPageShow = () => openStretch(state);
    for (const type of INPUT_EVENTS) {
      window.addEventListener(type, onInput, { capture: true, passive: true });
    }
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);

    const offQueries = queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== "updated") {
        return;
      }
      if (event.action.type === "error" && event.query.getObserversCount() > 0) {
        const detail = failureDetail(event.action.error, event.query.queryKey);
        if (detail) {
          track("error.read", detail);
        }
      }
      const watch = state.ready;
      if (watch && watch.armed && !watch.done) {
        checkReady(state, watch);
      }
    });
    const offMutations = queryClient.getMutationCache().subscribe((event) => {
      if (event.type === "updated" && event.action.type === "error") {
        const detail = failureDetail(event.action.error, event.mutation.options.mutationKey);
        if (detail) {
          track("error.write", detail);
        }
      }
    });

    return () => {
      for (const type of INPUT_EVENTS) {
        window.removeEventListener(type, onInput, { capture: true });
      }
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pageshow", onPageShow);
      offQueries();
      offMutations();
      state.stopTimer = window.setTimeout(() => {
        state.stopTimer = null;
        closeStretch(state, "route");
        state.pathname = null;
        stopUsage();
      }, 0);
    };
  }, [signedIn]);

  useEffect(() => {
    const state = stateRef.current;
    // The same path again (the second run of StrictMode, a new query string) is no new page.
    if (!signedIn || !state || state.pathname === pathname) {
      return;
    }
    closeStretch(state, "route");
    const route = routeTemplate(pathname);
    state.pathname = pathname;
    state.route = route;
    state.ready = null;
    setUsagePage(route, caseIdOf(pathname));
    if (route === null) {
      return;
    }
    const nav: Nav = documentViewed ? "route" : "load";
    documentViewed = true;
    track("page.view", { from: state.lastRoute, nav, slug: slugOf(pathname) });
    state.lastRoute = route;
    openStretch(state);
    watchReady(state, nav);
  }, [pathname, signedIn]);

  return null;
}

/**
 * The use of one dialog. When `open` turns true it reports "opened". `saved()` reports
 * "saved" with the time the dialog was open. A close or an unmount without `saved()`
 * reports "closed without a save". Nothing a person typed is reported.
 */
export function useDialogUsage(
  dialog: UsageDialog,
  open: boolean,
  caseId?: string | null,
): { saved: () => void } {
  const life = useRef<{ openedAt: number | null; saved: boolean; closeTimer: number | null }>({
    openedAt: null,
    saved: false,
    closeTimer: null,
  });
  const caseRef = useRef(caseId);
  useEffect(() => {
    caseRef.current = caseId;
  }, [caseId]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const state = life.current;
    const names = USAGE_DIALOG_EVENTS[dialog];
    if (state.closeTimer !== null) {
      // The second run of StrictMode: the dialog did not close.
      window.clearTimeout(state.closeTimer);
      state.closeTimer = null;
    }
    if (state.openedAt === null) {
      state.openedAt = Date.now();
      state.saved = false;
      track(names.open, {}, { caseId: caseRef.current });
    }
    return () => {
      // One task later, so a `saved()` in the same turn as the close still counts.
      state.closeTimer = window.setTimeout(() => {
        state.closeTimer = null;
        const openedAt = state.openedAt;
        state.openedAt = null;
        if (openedAt !== null && !state.saved) {
          track(names.cancel, {}, { caseId: caseRef.current, durationMs: Date.now() - openedAt });
        }
      }, 0);
    };
  }, [dialog, open]);

  const saved = useCallback(() => {
    const state = life.current;
    if (state.openedAt === null || state.saved) {
      return;
    }
    state.saved = true;
    track(
      USAGE_DIALOG_EVENTS[dialog].save,
      {},
      { caseId: caseRef.current, durationMs: Date.now() - state.openedAt },
    );
  }, [dialog]);

  return { saved };
}

/** A search of Help or the docs is reported this long after the last key. */
const CONTENT_SEARCH_WAIT_MS = 1_000;

/**
 * A search in Help or in the docs: one event a second after the last key, for a text
 * that is not empty. The log gets the length of the text and the number of hits, never
 * a word of it.
 */
export function useContentSearchUsage(where: "help" | "docs", query: string, hits: number): void {
  const latestHits = useRef(hits);
  useEffect(() => {
    latestHits.current = hits;
  }, [hits]);
  useEffect(() => {
    if (query.trim() === "") {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      track("content.search", { where, len: searchShape(query).len, hits: latestHits.current });
    }, CONTENT_SEARCH_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [where, query]);
}

/**
 * Report one event one time for `key` (a case id, a search). A `null` key reports
 * nothing. The detail is read when the key changes.
 */
export function useTrackOnce(
  name: UsageEventName,
  detail: UsageDetail,
  key: string | null,
  options?: TrackOptions,
): void {
  const last = useRef<string | null>(null);
  const latest = useRef({ detail, options });
  useEffect(() => {
    latest.current = { detail, options };
  });
  useEffect(() => {
    if (key === null || last.current === key) {
      return;
    }
    last.current = key;
    track(name, latest.current.detail, latest.current.options);
  }, [name, key]);
}
